/**
 * Module Tap to Pay — Adyen Terminal API on iPhone
 *
 * API publique :
 *   init(config)           → Configure Adyen SDK
 *   checkDevice()          → Verify device compatibility
 *   warmup()               → Pre-warm ProximityReader
 *   startPayment(request)  → Present amount → Card reader → Result
 *   refund(request)        → Linked refund via Adyen
 *   getReceipt(pspRef)     → Generate receipt data
 *   onEvent(listener)      → Subscribe to status/result events
 *
 * Native bridge (Capacitor) :
 *   Le flow de paiement tourne entièrement en natif via le SDK Adyen iOS.
 *   Ce module TS orchestre les appels et expose une API propre au JS.
 */

import type {
  TapToPayConfig,
  PaymentStatus,
  PaymentRequest,
  PaymentResult,
  RefundRequest,
  RefundResult,
  ReceiptData,
  TapToPayDeviceInfo,
  TapToPayEvent,
  DiagnoseResult,
} from './types';
import { AdyenTerminalClient } from './client';
import { emitEvent, onTapToPayEvent, getStatus } from './events';

export type {
  TapToPayConfig,
  PaymentRequest,
  PaymentResult,
  RefundRequest,
  RefundResult,
  ReceiptData,
  TapToPayDeviceInfo,
  TapToPayEvent,
  PaymentStatus,
  DiagnoseResult,
};
export { onTapToPayEvent, getStatus };

let client: AdyenTerminalClient | null = null;
let config: TapToPayConfig | null = null;

/**
 * Initialize the Tap to Pay module with Adyen credentials.
 * Must be called once before any payment.
 */
export async function init(cfg: TapToPayConfig): Promise<void> {
  config = cfg;
  client = new AdyenTerminalClient(cfg);
  emitEvent({ type: 'status_change', status: 'initializing' });

  try {
    // Pass backendUrl to native — the Swift plugin fetches sdkData itself
    // via /payments/pos-sessions when the Adyen SDK calls register(with:)
    const { TapToPayPlugin } = await import('./native');
    await TapToPayPlugin.initialize({
      environment: cfg.environment,
      merchantAccount: cfg.merchantAccount,
      storeId: cfg.storeId ?? '',
      backendUrl: cfg.backendUrl,
      countryCode: cfg.countryCode ?? 'FR',
      currency: cfg.currency ?? 'EUR',
    });

    emitEvent({ type: 'status_change', status: 'ready' });
  } catch (err: any) {
    emitEvent({ type: 'status_change', status: 'error' });
    emitEvent({ type: 'error', message: err?.message ?? 'Init failed' });
    throw err;
  }
}

/**
 * Check if the current device supports Tap to Pay.
 * iPhone XS+ with iOS 16.4+, passcode enabled.
 */
export async function checkDevice(): Promise<TapToPayDeviceInfo> {
  try {
    const { TapToPayPlugin } = await import('./native');
    return TapToPayPlugin.checkDevice();
  } catch {
    return {
      supported: false,
      iosVersion: 'unknown',
      model: 'unknown',
      passcodeEnabled: false,
      proximityReaderAvailable: false,
    };
  }
}

/**
 * Pre-warm the ProximityReader for faster first transaction.
 * Call after init(), ideally when the merchant opens the payment screen.
 */
export async function warmup(): Promise<void> {
  const { TapToPayPlugin } = await import('./native');
  await TapToPayPlugin.warmup();
}

/**
 * Start a payment. Displays the amount, presents the card reader,
 * processes via Adyen, returns the result.
 *
 * Apple requirement 2.1: amount must be clearly displayed before reader.
 * Apple requirement 1.5: one payment session at a time.
 */
export async function startPayment(request: PaymentRequest): Promise<PaymentResult> {
  if (getStatus() === 'waiting_for_card' || getStatus() === 'processing') {
    throw new Error('A payment is already in progress (Apple req 1.5)');
  }

  emitEvent({ type: 'status_change', status: 'waiting_for_card' });

  try {
    const { TapToPayPlugin } = await import('./native');
    const result: PaymentResult = await TapToPayPlugin.startPayment({
      amount: request.amount,
      currency: request.currency ?? config?.currency ?? 'EUR',
      reference: request.reference,
      shopperReference: request.shopperReference,
      metadata: request.metadata,
    });

    emitEvent({ type: 'status_change', status: result.status });
    emitEvent({ type: 'payment_result', result });
    return result;
  } catch (err: any) {
    const result: PaymentResult = {
      status: 'error',
      reference: request.reference,
      amount: request.amount,
      currency: request.currency ?? 'EUR',
      errorMessage: err?.message ?? 'Payment failed',
      timestamp: new Date().toISOString(),
    };
    emitEvent({ type: 'status_change', status: 'error' });
    emitEvent({ type: 'payment_result', result });
    return result;
  }
}

/**
 * Process a refund for a previous transaction.
 * Apple requirement 4.1: refund from same device or dashboard.
 * Apple requirement 4.2: full and partial refunds.
 */
export async function refund(request: RefundRequest): Promise<RefundResult> {
  if (!client) throw new Error('Module not initialized. Call init() first.');

  emitEvent({ type: 'status_change', status: 'refunding' });
  const result = await client.refund(request);
  emitEvent({ type: 'status_change', status: result.status === 'approved' ? 'ready' : 'error' });
  emitEvent({ type: 'refund_result', result });
  return result;
}

/**
 * Run Adyen SDK device diagnostic.
 * Calls getPaymentInterface(.tapToPay), isAccountLinkedForTapToPay, and warmUp()
 * to surface the exact AdyenPOSError blocking Tap to Pay.
 */
export async function diagnose(): Promise<DiagnoseResult> {
  const { TapToPayPlugin } = await import('./native');
  return TapToPayPlugin.diagnose();
}

/**
 * Trigger the Apple ID ↔ Adyen merchant linking flow for Tap to Pay.
 * Calls linkAccountForTapToPay() on the SDK — presents Apple's native UI.
 * Must call init() first.
 */
export async function linkAccount(): Promise<{ success: boolean; error?: string }> {
  const { TapToPayPlugin } = await import('./native');
  return TapToPayPlugin.linkAccount();
}

/**
 * Generate receipt data for a transaction.
 * Apple requirement 2.5: receipt via email, SMS, or print.
 */
export function buildReceipt(result: PaymentResult, merchantInfo: {
  name: string;
  address?: string;
}): ReceiptData {
  return {
    merchantName: merchantInfo.name,
    merchantAddress: merchantInfo.address,
    transactionType: 'payment',
    amount: result.amount,
    currency: result.currency,
    cardScheme: result.cardScheme,
    cardLastFour: result.cardLastFour,
    authCode: result.authCode,
    pspReference: result.pspReference ?? '',
    reference: result.reference,
    timestamp: result.timestamp,
    status: result.status,
  };
}
